import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  MapPinned, Plus, Search, Edit2, Trash2, X, AlertCircle, Loader2, CheckCircle,
} from 'lucide-react';
import { getAuthHeaders } from '../utils/authHeaders';

const API_BASE = process.env.REACT_APP_API_URL || 'https://predworks.alliantprojects.com/api/v1';

interface Constituency {
  id: number;
  name: string;
  code: string;
  description?: string;
  is_active: boolean;
}

type Form = Partial<Constituency>;

const EMPTY_FORM: Form = { name: '', code: '', description: '', is_active: true };

async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders(), ...(options.headers || {}) },
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.message ? (Array.isArray(e.message) ? e.message.join(', ') : e.message) : `Error ${res.status}`);
  }
  if (res.status === 204) return undefined as unknown as T;
  return res.json();
}

const unwrap = (j: any): any[] => (Array.isArray(j) ? j : (j?.data ?? j?.items ?? []));

// List API caps page size at 100; page through to fetch all.
async function fetchAllPages<T = any>(path: string): Promise<T[]> {
  const all: T[] = [];
  let page = 1;
  for (;;) {
    const sep = path.includes('?') ? '&' : '?';
    const resp = await api<any>(`${path}${sep}page=${page}&limit=100`);
    const rows = unwrap(resp);
    all.push(...(rows as T[]));
    if (!resp?.meta?.hasNext || rows.length === 0 || page > 100) break;
    page++;
  }
  return all;
}

const ConstituenciesManagement: React.FC = () => {
  const [items, setItems] = useState<Constituency[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<Form>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<Constituency | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const rows = await fetchAllPages<Constituency>('/constituencies?sortBy=code&sortOrder=ASC');
      setItems(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load constituencies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 3500);
    return () => clearTimeout(t);
  }, [notice]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...items].sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
    if (!q) return sorted;
    return sorted.filter((c) => [c.name, c.code, c.description].some((v) => String(v || '').toLowerCase().includes(q)));
  }, [items, search]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setFormError(null); setShowModal(true); };
  const openEdit = (c: Constituency) => { setEditingId(c.id); setForm({ ...c }); setFormError(null); setShowModal(true); };
  const setField = (k: keyof Form, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name?.trim()) { setFormError('Name is required'); return; }
    if (!form.code?.trim()) { setFormError('Code is required'); return; }
    try {
      setSaving(true);
      setFormError(null);
      const payload = {
        name: form.name.trim(),
        code: form.code.trim(),
        description: form.description?.trim() || undefined,
        is_active: form.is_active !== false,
      };
      if (editingId) {
        await api(`/constituencies/${editingId}`, { method: 'PUT', body: JSON.stringify(payload) });
        setNotice('Constituency updated successfully');
      } else {
        await api('/constituencies', { method: 'POST', body: JSON.stringify(payload) });
        setNotice('Constituency created successfully');
      }
      setShowModal(false);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Failed to save constituency');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await api(`/constituencies/${deleteTarget.id}`, { method: 'DELETE' });
      setNotice('Constituency deleted successfully');
      setDeleteTarget(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete constituency');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center">
          <MapPinned className="w-7 h-7 text-rose-600 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Assembly Constituencies</h1>
            <p className="text-sm text-gray-500">Constituencies available for linking to works</p>
          </div>
        </div>
        <button onClick={openCreate}
          className="inline-flex items-center px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors">
          <Plus className="w-4 h-4 mr-2" /> Add Constituency
        </button>
      </div>

      {notice && (
        <div className="mb-4 flex items-center bg-green-50 border border-green-200 text-green-800 rounded-lg px-4 py-2 text-sm">
          <CheckCircle className="w-4 h-4 mr-2" /> {notice}
        </div>
      )}
      {error && (
        <div className="mb-4 flex items-center bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-2 text-sm">
          <AlertCircle className="w-4 h-4 mr-2" /> {error}
        </div>
      )}

      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or code…"
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 text-sm" />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500"><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading constituencies…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <MapPinned className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            {items.length ? 'No constituencies match your search.' : 'No constituencies yet. Click “Add Constituency”.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Code', 'Name', 'Description', 'Active', ''].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-600 font-mono text-xs">{c.code}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{c.name}</td>
                    <td className="px-4 py-3 text-gray-500">{c.description || '—'}</td>
                    <td className="px-4 py-3">
                      {c.is_active
                        ? <span className="text-xs text-green-700 bg-green-100 px-1.5 py-0.5 rounded">Active</span>
                        : <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">Inactive</span>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(c)} title="Edit" className="p-1.5 text-gray-500 hover:text-rose-600 hover:bg-rose-50 rounded"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => setDeleteTarget(c)} title="Delete" className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded ml-1"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="text-xs text-gray-400 mt-2">{filtered.length} constituency(ies){search ? ` (filtered from ${items.length})` : ''}</p>

      {/* Create / Edit modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{editingId ? 'Edit Constituency' : 'Add Constituency'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              {formError && (
                <div className="flex items-center bg-red-50 border border-red-200 text-red-800 rounded-lg px-3 py-2 text-sm">
                  <AlertCircle className="w-4 h-4 mr-2" /> {formError}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Name *">
                  <input className={inp} value={form.name || ''} onChange={(e) => setField('name', e.target.value)} placeholder="e.g. Sirpur" />
                </Field>
                <Field label="Code *">
                  <input className={inp} value={form.code || ''} onChange={(e) => setField('code', e.target.value.toUpperCase())} placeholder="e.g. AC-001" />
                </Field>
              </div>
              <Field label="Description">
                <textarea className={inp} rows={2} value={form.description || ''} onChange={(e) => setField('description', e.target.value)} placeholder="Optional notes" />
              </Field>
              <label className="flex items-center text-sm text-gray-700">
                <input type="checkbox" className="mr-2 h-4 w-4 rounded border-gray-300 text-rose-600"
                  checked={form.is_active !== false} onChange={(e) => setField('is_active', e.target.checked)} />
                Active
              </label>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave} disabled={saving}
                className="inline-flex items-center px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-60">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingId ? 'Save Changes' : 'Create Constituency'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3"><Trash2 className="w-5 h-5 text-red-600" /></div>
              <h2 className="text-lg font-semibold text-gray-900">Delete Constituency</h2>
            </div>
            <p className="text-sm text-gray-600 mb-5">
              Delete <span className="font-medium">{deleteTarget.name}</span> ({deleteTarget.code})? Works already linked to it
              keep their reference, but it will no longer appear in the constituency dropdown.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} disabled={deleting}
                className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60">
                {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const inp = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 text-sm';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

export default ConstituenciesManagement;
